import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { Color, theme } from '../../constants'
import { Badge } from '../Badge'
import { Div } from '../Div'
import { Flex } from '../Flex'
import { Icon, IconName } from '../Icon'
import { Text } from '../Text'

type NavigationTab<T> = {
  icon: IconName
  label: string
  value: T
  isLocked?: boolean
  match?: RegExp
  badge?: { count: number; inFocus?: boolean }
  hidden?: boolean
}

type NavigationProps<T> = {
  value?: T
  tabs?: NavigationTab<T>[]
  onChange?: (newValue: T) => void
  title?: string
  children?: ReactNode
  center?: boolean
}

const Nav = styled.nav({
  margin: 0,
  padding: 0,
  display: 'flex',
  width: '100%',
  minHeight: 42,
  borderBottom: theme.border.style.thinLight,
  background: Color.WHITE,
})

const Inner = styled.div<{ center: boolean | undefined }>(({ center }) => ({
  display: 'flex',
  flexShrink: 2,
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  boxSizing: 'border-box' as const,
  margin: 0,
  paddingLeft: theme.spacing[2.5],
  paddingRight: theme.spacing[2.5],
  ...(center && {
    width: 1088,
    margin: '0 auto',
  }),
}))

const Tabs = styled(Flex)({
  flex: 1,
  gap: theme.spacing[1],
})

const Tab = styled.li<{ isLocked?: boolean }>(({ isLocked }) => ({
  margin: 0,
  listStyle: 'none',
  fontSize: 14,
  textAlign: 'center',
  display: 'center',
  color: isLocked ? Color.DARK_MEDIUM_GREY : Color.CHARCOAL,
  cursor: isLocked ? 'not-allowed' : 'pointer',
  height: 'fit-content',
}))

const Hover = styled.div<{ isActive: boolean; isLocked?: boolean }>(
  (props) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',

    padding: '4px 8px',
    borderRadius: 8,
    height: 'fit-content',

    color: Color.DARK_GREY,

    ':hover': props.isLocked
      ? undefined
      : {
          background: Color.LIGHT_GREY,
        },

    ...(props.isActive && {
      background: Color.LIGHT_GREY,
      color: Color.CHARCOAL,

      ':after': {
        display: 'block',
        content: '""',
        width: '100%',
        height: 2,
        background: Color.BLUE,
        position: 'absolute',
        borderRadius: 1,
        bottom: -8,
        left: 0,
      },
    }),
  }),
)

export const Navigation = <T extends string | number | boolean>({
  onChange,
  tabs,
  value,
  title,
  center,
  children,
}: NavigationProps<T>) => {
  return (
    <Nav>
      <Inner center={center}>
        {title && (
          <Badge styles={{ mr: 3 }} color="white" squared>
            <Text>{title}</Text>
          </Badge>
        )}
        <Tabs as="ul">
          {tabs
            ?.filter(({ hidden }) => !hidden)
            .map((option) => (
              <Tab
                key={option.label}
                onClick={() => !option.isLocked && onChange?.(option.value)}
                isLocked={option.isLocked}
              >
                <Hover
                  isActive={
                    option.match
                      ? typeof value === 'string' && option.match.test(value)
                      : option.value === value
                  }
                  isLocked={option.isLocked}
                >
                  <Icon name={option.icon} mr={2} size="sm" />
                  <Text noSelect noWrap fontWeight={600}>
                    {option.label}
                  </Text>
                  {option.badge && !option.isLocked && (
                    <Div
                      styles={{
                        borderRadius: theme.border.radius.normal,
                        fontWeight: theme.font.weight.medium,
                        fontSize: 11,
                        color: option.badge.inFocus
                          ? Color.WHITE
                          : Color.DARKER_GREY,
                        background: option.badge.inFocus
                          ? Color.BLUE
                          : Color.GREY,
                        ml: 2,
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      {option.badge.count}
                    </Div>
                  )}
                </Hover>
              </Tab>
            ))}
        </Tabs>
        {children}
      </Inner>
    </Nav>
  )
}
